import {isElementHidden} from "./utils.ts";

/**
 * Miscellaneous non-React scripts to load on each page.
 */
export default class MiscScripts {
    constructor() {
        console.log("MiscScripts::constructor");
    };

    /**
     * On home page of broker sites, some will have autoplay video as main 'hero' component, while others have the
     * traditional carousel & search-bar module. For those with video, we must wait for video to end playing, and
     * after video finishes, we automatically switch to the default module showing the broker name and search bar.
     * Note: for this script to work, the Elementor 'mod-image' module must use Dynamic.ooo Visibility as follows:
     *  - Visibility = YES
     *  - Always hide this element = YES
     *  - Keep HTML = YES
     *  If a site has no video, then do the same for the 'mod-video' module and leave the 'mod-image' intact.
     */
    homeAutoSwitchVideoAndCarouselModules() {
        // 1 - check if we're on home page; else this does not apply
        const body = document.querySelector("body");
        if (!body?.classList.contains("home"))
            return;

        console.log("banner-switcher :: on home page");
        // 2 - check if there's an active video module
        const videoModule = body.querySelector<HTMLDivElement>("#mod-video");
        if (isElementHidden(videoModule))
            return;

        console.log("banner-switcher :: video module is active");
        const carouselModule = body.querySelector<HTMLDivElement>("#mod-image");
        const videoPlayer = videoModule!.querySelector<HTMLVideoElement>("video") ?? null;

        console.log("banner-switcher :: video player exists", videoPlayer);
        videoPlayer?.addEventListener("ended", () => {
            console.log(`hero video has finished playing, switch modules`);
            setTimeout(() => {
                videoModule!.style.display = 'none';
                if (carouselModule) {
                    carouselModule.classList.remove("dce-visibility-element-hidden");
                    carouselModule.style.display = 'flex !important;';
                }
            }, 500);
        });
    }

    /**
     * @deprecated 2024-10 I tried this to force Elementor pagination to scroll up after reloading on page switch,
     *  but for some reason, Elementor does a weird page reload, which does not trigger a DOMContentLoaded, and thus
     *  this code never worked for Elementor. Instead, I updated FacetWP and used a Facet Pager which, since 4.13,
     *  includes auto-scroll to top on pagination.
     *
     * In browsers, the default behaviour is to keep the user's scroll position so that when navigating through
     * pages of paginated layout or opening a link and going back, the browser will bring the user back to where
     * he came from and thus allow him to continue reading from there. However, this causes a problem with
     * Elementor Loop Grid pagination because when we switch pages, we expect to be brought back to the top of the
     * page and not at the bottom and have to manually scroll up. Therefore, this simple hack forces every single
     * page to auto-scroll to position 0,0 on page load.
     */
    autoScrollElementorPagination() {
        console.log(`MiscScripts::autoScrollElementorPagination`);

        // after 1s delay, scroll to top of page
        document.addEventListener("DOMContentLoaded", () => {
            setTimeout(() => {
                console.log(`load-top`);
                // window.scrollTo(0, 0);
                window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
            }, 1_000);
        });
    }

    /**
     * The new WPML ATE (Advanced Translation Editor) does not allow translating ampersands (&), so while WPML
     * is working on a fix, we need to find workarounds to allow writing '&' in translations.
     * @source
     * - https://stackoverflow.com/questions/36303869/how-to-use-document-evaluate-and-xpath-to-get-a-list-of-elements
     * - https://developer.mozilla.org/en-US/docs/Web/API/Document/evaluate
     * - https://stackoverflow.com/questions/76499112/how-to-get-dom-that-textcontent-contains-something-text
     * - https://www.roborabbit.com/blog/everything-you-need-to-know-about-the-xpath-contains-function/
     */
    fixWpmlAmpersand() {
        console.log(`MiscScripts::fixWpmlAmpersand`);

        const needle = "{@ev-amp}";

        // search needle in 'textContent' of nodes
        const textNodes = document.evaluate(`//*[contains(text(), "${needle}")]`,
            document.body, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE);

        // best to use snapshots since it allows modifying content in-place (iterator invalidates the cursor on edit)
        for (let i = 0; i < textNodes.snapshotLength; i++) {
            const node = textNodes.snapshotItem(i);
            if (node == null) continue;

            console.log(`ampersand-node.textContent`, node);
            // edit text content of each node matching our needle
            node.textContent = node?.textContent?.replace(needle, "&") ?? "";
        }


        // Note: do NOT replace code in <input>.value because it may prevent GF form submission (consent checkbox)
        // search needle in 'value' of nodes
        // const valueNodes = document.evaluate(`//*[contains(@value, "${needle}")]`,
        //     document.body, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE);
        // for (let i = 0; i < valueNodes.snapshotLength; i++) {
        //     const node = valueNodes.snapshotItem(i) as HTMLInputElement | any;
        //     if (node == null) continue;
        //
        //     console.log(`ampersand-node.value`, node);
        //     // edit text content of each node matching our needle
        //     node.value = node?.value?.replace(needle, "&") ?? "";
        // }
    }

    /**
     * @deprecated 2024-08 We switched to PopupMaker which is officially recommended by GravityForms, since the
     * Elementor hacky solution no longer seems to work reliably. For now, it does not hurt, but we'll remove soon.
     *
     * Elementor popups do not handle well GF forms (e.g. validation and input masks). There is no official
     * Elementor solution, so we need this trick to bypass our issues.
     * @source
     * - https://www.reddit.com/r/Wordpress/comments/158ilce/gravity_forms_plugin_elementor_popup_fix/
     * - https://github.com/elementor/elementor/issues/7245#issuecomment-1500990456
     * - https://github.com/elementor/elementor/issues/7245
     * - https://docs.gravityforms.com/gform_post_render/
     */
    fixGfPopupForms() {
        console.log(`MiscScripts::fixGfPopupForms`);

        // avoid uncaught JS error if jQuery is not defined in certain pages
        let _jquery = null;
        try {
            // @ts-ignore
            _jquery = jQuery;
        } catch (e) {
            console.log("React: jQuery not found; nothing to worry about; just skipping.");
        }

        // listen to moment when Elementor popup opens
        _jquery?.(document).on('elementor/popup/show', () => {
            // 1 - check if there's a GF form in that popup and retrieve its id
            const form: HTMLFormElement | null = document.querySelector(".gform_wrapper form");
            const formId = form?.dataset.formid ?? null; // GF stores form id in "data-formid=<id>"

            console.log(`form-found`, {form, formId});
            if (formId == null) return;

            // 2 - trigger GF function to re-render the form and properly load scripts needed for validation, etc.
            _jquery?.(document).trigger('gform_post_render', [formId, 1]);
        });
    }
}